import { useState, useRef, useEffect, useCallback } from 'react';
import { PageHeader, Skeleton } from '@solid/ui';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
  DragOverEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  CheckSquare,
  Plus,
  Check,
  AlertCircle,
  Calendar,
  GripVertical,
  Clock,
  MessageSquare,
  ChevronRight,
} from 'lucide-react';
import { tasksApi, projectsApi } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Avatar } from '@/components/ui/Avatar';
import { CreateTaskListModal } from '@/components/modals/CreateTaskListModal';
import { TaskDetailModal } from '@/components/modals/TaskDetailModal';
import { cn } from '@/lib/utils';
import { useAuthStore, getIsSystemAdmin } from '@/stores/auth.store';
import { useProjectRoles } from '@/hooks/useProjectRoles';

interface Task {
  id: string;
  title: string;
  description?: string;
  status: string;
  priority: string;
  dueDate?: string;
  assignee?: {
    id: string;
    name: string;
    avatarUrl?: string;
  };
  _count?: {
    comments: number;
  };
}

interface TaskList {
  id: string;
  name: string;
  tasks: Task[];
}

const priorityConfig = {
  LOW: { color: 'text-grey-500', label: 'Low' },
  MEDIUM: { color: 'text-blue-500', label: 'Medium' },
  HIGH: { color: 'text-orange-500', label: 'High' },
  URGENT: { color: 'text-error', label: 'Urgent' },
};

function SortableTask({
  task,
  listId,
  onClick,
  userId,
  onUpdateStatus,
  isNew,
  onNewHighlightEnd,
}: {
  task: Task;
  listId: string;
  onClick: () => void;
  userId?: string;
  onUpdateStatus?: (status: string) => void;
  isNew?: boolean;
  onNewHighlightEnd?: () => void;
}) {
  const isCompleted = task.status === 'COMPLETED';
  const isInProgress = task.status === 'IN_PROGRESS';
  const isAssignee = (task as any).assigneeId === userId;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: task.id,
    disabled: isCompleted, // Disable dragging for completed tasks
    data: {
      type: 'task',
      task,
      listId,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const elementRef = useRef<HTMLDivElement | null>(null);
  const combinedRef = useCallback((node: HTMLDivElement | null) => {
    setNodeRef(node);
    elementRef.current = node;
  }, [setNodeRef]);

  useEffect(() => {
    if (isNew && elementRef.current) {
      elementRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      const timer = setTimeout(() => onNewHighlightEnd?.(), 3000);
      return () => clearTimeout(timer);
    }
  }, [isNew]);

  const priority = priorityConfig[task.priority as keyof typeof priorityConfig] || priorityConfig.MEDIUM;
  // Compare dates in UTC to avoid timezone issues
  const isOverdue = task.dueDate && (() => {
    const dueDate = new Date(task.dueDate);
    const today = new Date();
    const dueDateUTC = Date.UTC(dueDate.getUTCFullYear(), dueDate.getUTCMonth(), dueDate.getUTCDate());
    const todayUTC = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
    return dueDateUTC < todayUTC;
  })() && !isCompleted;

  // Simplified view for completed tasks - only show title
  if (isCompleted) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className="bg-white rounded-lg border border-grey-100 p-2 opacity-60 cursor-pointer hover:opacity-80 transition-all"
        onClick={onClick}
      >
        <div className="flex items-center gap-2">
          <span className="flex-shrink-0 w-4 h-4 rounded-full bg-success text-white flex items-center justify-center">
            <Check className="w-2.5 h-2.5" />
          </span>
          <p className="text-grey-500 line-through text-sm truncate">{task.title}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={combinedRef}
      style={style}
      className={cn(
        'bg-white rounded-lg border border-grey-200 p-3 shadow-sm group',
        'hover:border-accent/30 hover:shadow transition-all cursor-pointer',
        isDragging && 'opacity-50 shadow-elevated rotate-2',
        isNew && 'border-2 border-orange-400'
      )}
      onClick={onClick}
    >
      <div className="flex items-start gap-2">
        <button
          className="mt-0.5 touch-none cursor-grab active:cursor-grabbing"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="w-4 h-4 text-grey-300 group-hover:text-grey-400" />
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <p className="font-medium text-sm text-secondary">
              {task.title}
            </p>
            {(task.priority === 'HIGH' || task.priority === 'URGENT') && (
              <AlertCircle className={cn('w-4 h-4 flex-shrink-0', priority.color)} />
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* In Progress toggle button - only for assignees */}
            {isAssignee && onUpdateStatus && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onUpdateStatus(isInProgress ? 'PENDING' : 'IN_PROGRESS');
                }}
                className={cn(
                  'flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full transition-colors',
                  isInProgress
                    ? 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                    : 'bg-grey-100 text-grey-500 hover:bg-grey-200'
                )}
              >
                <Clock className="w-3 h-3" />
                {isInProgress ? 'In Progress' : 'Start'}
              </button>
            )}

            {/* Show status badge for non-assignees when in progress */}
            {!isAssignee && isInProgress && (
              <span className="text-xs px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-500">
                In Progress
              </span>
            )}

            {/* Comments count */}
            {(task._count?.comments || 0) > 0 && (
              <div className="flex items-center gap-1 text-xs text-grey-400">
                <MessageSquare className="w-3 h-3" />
                {task._count.comments}
              </div>
            )}

            {/* Spacer */}
            <div className="flex-1" />

            {/* Due date - right aligned */}
            {task.dueDate && (
              <div className={cn(
                'flex items-center gap-1 text-xs flex-shrink-0',
                isOverdue ? 'text-error' : 'text-grey-500'
              )}>
                {isOverdue ? (
                  <AlertCircle className="w-3 h-3" />
                ) : (
                  <Calendar className="w-3 h-3" />
                )}
                {new Date(task.dueDate).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  timeZone: 'UTC',
                })}
              </div>
            )}
          </div>

          {task.assignee && (
            <div className="mt-2 flex items-center gap-1.5">
              <Avatar
                src={task.assignee.avatarUrl}
                name={task.assignee.name}
                size="xs"
              />
              <span className="text-xs text-grey-500 truncate">
                {task.assignee.name}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TaskListColumn({
  list,
  projectId,
  projectMembers,
  onTaskClick,
  userId,
  canCreateTask = true,
  onUpdateTaskStatus,
}: {
  list: TaskList;
  projectId: string;
  projectMembers: any[];
  onTaskClick: (task: Task, listId: string) => void;
  userId?: string;
  canCreateTask?: boolean;
  onUpdateTaskStatus?: (taskId: string, status: string) => void;
}) {
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [newTaskId, setNewTaskId] = useState<string | null>(null);
  const openTasks = list.tasks?.filter((t) => t.status !== 'COMPLETED') || [];
  const completedTasks = list.tasks?.filter((t) => t.status === 'COMPLETED') || [];

  return (
    <div className="bg-grey-50 rounded-xl p-3 flex flex-col min-w-[280px]">
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="flex items-center gap-2">
          <Link
            to={`/projects/${projectId}/tasks/${list.id}`}
            className="font-semibold text-secondary hover:text-accent transition-colors"
          >
            {list.name}
          </Link>
          <span className="text-xs text-grey-400 bg-grey-200 px-1.5 py-0.5 rounded">
            {openTasks.length}
          </span>
        </div>
        {canCreateTask && (
          <button
            onClick={() => setShowCreateTask(true)}
            className="p-1 hover:bg-grey-200 rounded transition-colors"
          >
            <Plus className="w-4 h-4 text-grey-500" />
          </button>
        )}
      </div>

      <div className="space-y-2 min-h-[60px]">
        <SortableContext
          items={openTasks.map((t) => t.id)}
          strategy={verticalListSortingStrategy}
        >
          {openTasks.map((task) => (
            <SortableTask
              key={task.id}
              task={task}
              listId={list.id}
              onClick={() => onTaskClick(task, list.id)}
              userId={userId}
              onUpdateStatus={onUpdateTaskStatus ? (status) => onUpdateTaskStatus(task.id, status) : undefined}
              isNew={task.id === newTaskId}
              onNewHighlightEnd={() => setNewTaskId(null)}
            />
          ))}
        </SortableContext>

        {openTasks.length === 0 && completedTasks.length === 0 && (
          <div className="py-6 text-center text-grey-400 text-sm">
            No tasks yet
          </div>
        )}
      </div>

      {/* Completed tasks section */}
      {completedTasks.length > 0 && (
        <div className="mt-3 pt-3 border-t border-grey-200">
          <button
            onClick={() => setShowCompleted(!showCompleted)}
            className="flex items-center gap-2 text-sm text-grey-500 hover:text-secondary transition-colors w-full"
          >
            <Check className="w-3.5 h-3.5" />
            {completedTasks.length} completed
            <span className={cn(
              "ml-auto transition-transform",
              showCompleted && "rotate-180"
            )}>
              ▼
            </span>
          </button>

          {showCompleted && (
            <div className="mt-2 space-y-2">
              <SortableContext
                items={completedTasks.map((t) => t.id)}
                strategy={verticalListSortingStrategy}
              >
                {completedTasks.map((task) => (
                  <SortableTask
                    key={task.id}
                    task={task}
                    listId={list.id}
                    onClick={() => onTaskClick(task, list.id)}
                    userId={userId}
                  />
                ))}
              </SortableContext>
            </div>
          )}
        </div>
      )}

      {canCreateTask && (
        <button
          onClick={() => setShowCreateTask(true)}
          className="mt-2 w-full py-2 text-sm text-grey-500 hover:text-accent hover:bg-grey-100 rounded-lg transition-colors flex items-center justify-center gap-1"
        >
          <Plus className="w-4 h-4" />
          Add task
        </button>
      )}

      <TaskDetailModal
        create
        isOpen={showCreateTask}
        onClose={() => setShowCreateTask(false)}
        projectId={projectId}
        listId={list.id}
        onTaskCreated={(id) => setNewTaskId(id)}
      />
    </div>
  );
}

function DragOverlayContent({ task }: { task: Task }) {
  const isCompleted = task.status === 'COMPLETED';
  const priority = priorityConfig[task.priority as keyof typeof priorityConfig] || priorityConfig.MEDIUM;

  return (
    <div className="bg-white rounded-lg border-2 border-accent p-3 shadow-elevated min-w-[280px] max-w-sm rotate-3">
      <div className="flex items-start gap-2">
        <GripVertical className="w-4 h-4 text-grey-400 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p className={cn(
              'font-medium text-sm',
              isCompleted ? 'text-grey-500 line-through' : 'text-secondary'
            )}>
              {task.title}
            </p>
            {(task.priority === 'HIGH' || task.priority === 'URGENT') && (
              <AlertCircle className={cn('w-4 h-4 flex-shrink-0', priority.color)} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function TaskBoard() {
  const { projectId } = useParams<{ projectId: string }>();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  // Staff on THIS project: Solid anywhere, a partner agency on its own
  const { isProjectStaff: isAgencyMember } = useProjectRoles(projectId);
  const isSystemAdmin = getIsSystemAdmin(user);
  const [showCreateList, setShowCreateList] = useState(false);
  const [selectedTask, setSelectedTask] = useState<{ task: Task; listId: string } | null>(null);
  const [activeTask, setActiveTask] = useState<Task | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const { data: project, isLoading: projectLoading } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.get(projectId!),
    enabled: !!projectId,
  });

  const { data: taskLists, isLoading: listsLoading } = useQuery({
    queryKey: ['taskLists', projectId],
    queryFn: () => tasksApi.listTaskLists(projectId!),
    enabled: !!projectId,
  });

  // Fetch tasks for each list
  const taskListsWithTasks = useQuery({
    queryKey: ['taskBoard', projectId],
    queryFn: async () => {
      if (!taskLists) return [];

      const listsWithTasks = await Promise.all(
        taskLists.map(async (list: any) => {
          const tasks = await tasksApi.listTasks(projectId!, list.id);
          return { ...list, tasks };
        })
      );

      return listsWithTasks;
    },
    enabled: !!taskLists && taskLists.length > 0,
  });

  const moveTaskMutation = useMutation({
    mutationFn: ({ taskId, listId, position }: { taskId: string; listId: string; position?: number }) =>
      tasksApi.moveTask(taskId, listId, position),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taskBoard', projectId] });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    },
  });

  // Update task status (for In Progress toggle)
  const updateStatusMutation = useMutation({
    mutationFn: ({ taskId, status }: { taskId: string; status: string }) =>
      tasksApi.updateTask(taskId, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taskBoard', projectId] });
      queryClient.invalidateQueries({ queryKey: ['taskLists', projectId] });
    },
  });

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const task = active.data.current?.task;
    if (task) {
      setActiveTask(task);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveTask(null);

    if (!over) return;

    const activeData = active.data.current;
    const overData = over.data.current;

    if (!activeData || activeData.type !== 'task') return;

    const activeTaskId = active.id as string;
    const sourceListId = activeData.listId;

    // Find the target list
    let targetListId: string;
    let targetPosition: number | undefined;

    if (overData?.type === 'task') {
      // Dropped on another task
      targetListId = overData.listId;
      const targetList = taskListsWithTasks.data?.find((l: TaskList) => l.id === targetListId);
      const openTasks = (targetList?.tasks || []).filter((t: Task) => t.status !== 'COMPLETED');
      targetPosition = openTasks.findIndex((t: Task) => t.id === over.id);
    } else {
      // Dropped on the list itself (empty area)
      targetListId = over.id as string;
      targetPosition = 0;
    }

    // Only update if moved to a different list
    if (sourceListId !== targetListId) {
      moveTaskMutation.mutate({
        taskId: activeTaskId,
        listId: targetListId,
        position: targetPosition,
      });
    }
  };

  const handleDragOver = (_event: DragOverEvent) => {
    // Can be used for visual feedback during drag
  };

  const projectMembers = project?.members || [];
  const clientsCanCreate = (project?.settings as any)?.clientsCanCreateTasks !== false;
  const canCreateTask = isAgencyMember || clientsCanCreate;
  const lists: TaskList[] = taskListsWithTasks.data || [];
  const isLoading = projectLoading || listsLoading || taskListsWithTasks.isLoading;

  if (isLoading) {
    return (
      <div aria-busy>
        <Skeleton className="mb-4 h-6 w-32" />
        <Skeleton className="mb-8 h-10 w-64" />
        <div className="flex gap-4 overflow-x-auto">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-96 w-80 shrink-0 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-grey-500 mb-4">
        {(project?.clientOrganization || project?.organization) && (() => {
          const displayOrg = project.clientOrganization ?? project.organization!;
          return (
            <>
              <Link
                to={`/projects?organizationId=${displayOrg.id}`}
                className="hover:text-primary"
              >
                <span className="badge-accent text-xs">{displayOrg.name}</span>
              </Link>
              <ChevronRight className="w-4 h-4" />
            </>
          );
        })()}
        <Link to={`/projects/${projectId}`} className="hover:text-accent">
          {project?.name}
        </Link>
        <ChevronRight className="w-4 h-4" />
        <span>All Tasks</span>
      </div>

      <PageHeader
        title="All Tasks"
        subtitle="Drag tasks between lists to reorganize"
        actions={
          <Button onClick={() => setShowCreateList(true)}>
            <Plus className="w-4 h-4" />
            Add List
          </Button>
        }
      />

      {/* Board */}
      {lists.length === 0 ? (
        <div className="py-16 text-center">
          <CheckSquare className="w-16 h-16 text-grey-200 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-secondary mb-2">No task lists yet</h3>
          <p className="text-grey-500 mb-6">Create your first list to start organizing tasks</p>
          <Button onClick={() => setShowCreateList(true)}>
            <Plus className="w-4 h-4" />
            Create First List
          </Button>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragOver={handleDragOver}
        >
          {/* Responsive grid: 1 col on mobile, 2 on md, 3 on lg, max 4 on xl */}
          {/* Lists expand to fill available space when fewer than max per row */}
          <div className={cn(
            "grid gap-4",
            lists.length === 1 && "grid-cols-1 max-w-xl",
            lists.length === 2 && "grid-cols-1 md:grid-cols-2",
            lists.length === 3 && "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
            lists.length >= 4 && "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          )}>
            {lists.map((list) => (
              <TaskListColumn
                key={list.id}
                list={list}
                projectId={projectId!}
                projectMembers={projectMembers}
                onTaskClick={(task, listId) => setSelectedTask({ task, listId })}
                userId={user?.id}
                canCreateTask={canCreateTask}
                onUpdateTaskStatus={isAgencyMember ? (taskId, status) => updateStatusMutation.mutate({ taskId, status }) : undefined}
              />
            ))}

            {/* Add list button */}
            <button
              onClick={() => setShowCreateList(true)}
              className="min-h-[120px] border-2 border-dashed border-grey-200 rounded-xl flex items-center justify-center gap-2 text-grey-400 hover:text-accent hover:border-accent transition-colors"
            >
              <Plus className="w-5 h-5" />
              Add another list
            </button>
          </div>

          <DragOverlay>
            {activeTask ? <DragOverlayContent task={activeTask} /> : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* Modals */}
      <CreateTaskListModal
        isOpen={showCreateList}
        projectId={projectId!}
        onClose={() => setShowCreateList(false)}
        isAgencyMember={isAgencyMember}
        isSystemAdmin={isSystemAdmin}
      />

      {selectedTask && project && (
        <TaskDetailModal
          isOpen={!!selectedTask}
          onClose={() => setSelectedTask(null)}
          task={selectedTask.task}
          projectId={projectId!}
          listId={selectedTask.listId}
          organizationId={project.organizationId}
        />
      )}
    </div>
  );
}
